import { phaseLabel } from "@/lib/phase";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Lock, Check, ChevronRight, Dumbbell, ArrowRight, Flame } from "lucide-react";

const PHASE_STYLE = {
  RESET:     { bg: "bg-primary",       text: "text-primary-foreground", dot: "bg-primary" },
  ESTABLISH: { bg: "bg-blue-600",      text: "text-white",              dot: "bg-blue-600" },
  PROGRESS:  { bg: "bg-emerald-600",   text: "text-white",              dot: "bg-emerald-600" },
  "OWN IT":  { bg: "bg-foreground",    text: "text-background",         dot: "bg-foreground" },
};

const LEAN_PHASE_COLOR = { CALIBRATE: "#E65322", CREATE: "#2A6FDB", REFINE: "#2F8F4E", FINISH: "#7A4CC2" };
const BUILD_PHASE_COLOR = { BASELINE: "#E65322", BUILD: "#2A6FDB", OVERLOAD: "#2F8F4E", CONSOLIDATE: "#7A4CC2" };

function BuildTrainView() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [activeWeek, setActiveWeek] = useState(null);
  const nav = useNavigate();

  const load = (weekOverride) => {
    api.get("/pathway/state").then(({ data: pw }) => {
      if (!pw.block) return;
      const wk = weekOverride ?? activeWeek ?? pw.block.current_week;
      api.get(`/build/week/${wk}`).then(({ data: w }) => {
        setData({ pw, week: w });
        if (activeWeek === null) setActiveWeek(pw.block.current_week);
      });
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const pickWeek = (n) => {
    setActiveWeek(n);
    load(n);
  };

  const logWorkout = async (letter) => {
    setBusy(letter);
    try {
      const { data: res } = await api.post("/build/log/workout", { week: data.week.week, letter });
      toast.success(`Workout ${letter} logged (${res.workouts_logged}/${res.workouts_target} this week).`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not log workout."); }
    finally { setBusy(null); }
  };
  const unlogWorkout = async (letter) => {
    setBusy(letter);
    try {
      await api.delete(`/build/log/workout?week=${data.week.week}&letter=${letter}`);
      toast.success(`Workout ${letter} unlogged.`);
      load();
    } catch (e) { toast.error("Could not unlog workout."); }
    finally { setBusy(null); }
  };

  if (!data) return <div className="text-muted-foreground uppercase tracking-wider text-sm">Loading BUILD train…</div>;
  const { pw, week } = data;
  const currentWeek = pw.block.current_week;
  const phaseMap = { BASELINE: [1,2,3], BUILD: [4,5,6], OVERLOAD: [7,8,9], CONSOLIDATE: [10,11,12] };
  const weeks = Array.from({length: 12}, (_,i) => i+1);
  return (
    <div className="space-y-6" data-testid="train-build">
      <div>
        <div className="label-eyebrow">Train · BUILD · {pw.block.track}</div>
        <h1 className="wordmark mt-3 text-4xl md:text-5xl uppercase">Your BUILD Workouts.</h1>
        <div className="mt-3 inline-flex items-center gap-2 rounded-md px-3 py-1.5 font-head uppercase tracking-widest text-xs text-white" style={{background: BUILD_PHASE_COLOR[week.phase]}}>
          <Flame size={12}/> {week.phase} · Week {week.week} · {pw.block.track}
        </div>
        <p className="mt-4 max-w-xl text-sm text-muted-foreground">Three sessions this week. Progress reps → load → selective volume, in that order. Maintaining honest RIR is a positive result.</p>
      </div>

      {/* 12-Week + Phase picker */}
      <div className="bg-card border border-border rounded-lg p-4" data-testid="build-week-picker-wrap">
        <div className="label-eyebrow mb-3">Pick a week</div>
        <div className="grid grid-cols-6 md:grid-cols-12 gap-1.5" data-testid="train-build-week-picker">
          {weeks.map((wk) => {
            const ph = Object.entries(phaseMap).find(([, ws]) => ws.includes(wk))[0];
            const color = BUILD_PHASE_COLOR[ph];
            const locked = wk > currentWeek;
            const active = wk === week.week;
            return (
              <button key={wk} disabled={locked} onClick={() => pickWeek(wk)}
                className={`aspect-square rounded-md border flex flex-col items-center justify-center transition-colors ${
                  locked ? "bg-muted text-muted-foreground border-border cursor-not-allowed opacity-60" :
                  active ? "text-white border-transparent ring-2 ring-primary" :
                  "bg-background border-border hover:border-primary"
                }`}
                style={active ? {background: color} : {}}
                data-testid={`train-build-week-${wk}`}>
                {locked && <Lock size={10} className="opacity-70"/>}
                {!locked && <span className="opacity-70 text-[8px] uppercase tracking-widest">Wk</span>}
                <span className="wordmark text-lg">{wk}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap gap-3 text-[10px] uppercase tracking-widest">
          {Object.entries(BUILD_PHASE_COLOR).map(([ph, c]) => (
            <div key={ph} className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded" style={{background: c}}/> {ph} · W{phaseMap[ph][0]}–{phaseMap[ph][2]}
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4">
        {week.workouts.map((w) => (
          <div key={w.letter} className="bg-card border border-border rounded-lg p-6" data-testid={`train-build-workout-${w.letter}`}>
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <div className="font-head uppercase text-xl">Workout {w.letter}</div>
                {w.logged && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-head uppercase tracking-widest"
                    data-testid={`train-build-workout-${w.letter}-logged-badge`}>
                    ✓ Logged {w.logged_at}
                  </span>
                )}
              </div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">{pw.block.track}</div>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{w.focus}</p>
            {w.image && (
              <a href={w.image} target="_blank" rel="noreferrer" className="mt-4 block rounded-md overflow-hidden border border-border" data-testid={`train-build-workout-${w.letter}-image`}>
                <img src={w.image} alt={`BA30 BUILD ${week.phase} Workout ${w.letter} — ${pw.block.track} track illustration`} loading="lazy" className="block w-full h-auto" />
              </a>
            )}
            <table className="mt-4 w-full text-sm">
              <thead className="text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="text-left py-2">Exercise</th>
                  <th className="text-center py-2">Sets</th>
                  <th className="text-center py-2">Reps</th>
                  <th className="text-center py-2">RIR</th>
                </tr>
              </thead>
              <tbody>
                {w.exercises.map((ex, i) => (
                  <tr key={i} className="border-b border-border/60 last:border-0">
                    <td className="py-2.5 pr-2">
                      <div className="font-medium">{ex.name}</div>
                      {ex.coaching && <div className="text-[11px] text-muted-foreground mt-0.5">{ex.coaching}</div>}
                    </td>
                    <td className="text-center py-2.5">{ex.sets}</td>
                    <td className="text-center py-2.5">{ex.reps}</td>
                    <td className="text-center py-2.5">{ex.rir}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              {week.week === currentWeek ? (
                !w.logged ? (
                  <button onClick={() => logWorkout(w.letter)} disabled={busy===w.letter}
                    className="btn-primary" data-testid={`train-build-log-workout-${w.letter}`}>
                    {busy===w.letter ? "Logging…" : `Log Workout ${w.letter}`}
                  </button>
                ) : (
                  <button onClick={() => unlogWorkout(w.letter)} disabled={busy===w.letter}
                    className="text-xs underline text-muted-foreground hover:text-foreground"
                    data-testid={`train-build-unlog-workout-${w.letter}`}>
                    Undo log
                  </button>
                )
              ) : (
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                  {week.week < currentWeek ? "Past week — logging locked" : "Future week — preview only"}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-2 text-xs uppercase tracking-widest text-muted-foreground" data-testid="train-build-week-progress">
        {week.workouts_logged ?? 0} of {week.workouts_target ?? 3} sessions logged this week
      </div>

      <button onClick={() => nav(`/app/build/week/${week.week}`)} className="btn-primary" data-testid="train-build-open-week">
        Open Week {week.week} <ArrowRight size={14} className="ml-1"/>
      </button>
    </div>
  );
}

function LeanTrainView() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const nav = useNavigate();
  const load = () => {
    api.get("/pathway/state").then(({ data: pw }) => {
      if (!pw.block) return;
      api.get(`/lean/week/${pw.block.current_week}`).then(({ data: w }) => setData({ pw, week: w }));
    });
  };
  useEffect(() => { load(); }, []);

  const logWorkout = async (letter) => {
    setBusy(letter);
    try {
      const { data: res } = await api.post("/lean/log/workout", { week: data.week.week, letter });
      toast.success(`Workout ${letter} logged (${res.workouts_logged}/${res.workouts_target} this week).`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not log workout."); }
    finally { setBusy(null); }
  };
  const unlogWorkout = async (letter) => {
    setBusy(letter);
    try {
      await api.delete(`/lean/log/workout?week=${data.week.week}&letter=${letter}`);
      toast.success(`Workout ${letter} unlogged.`);
      load();
    } catch (e) { toast.error("Could not unlog workout."); }
    finally { setBusy(null); }
  };

  if (!data) return <div className="text-muted-foreground uppercase tracking-wider text-sm">Loading LEAN train…</div>;
  const { pw, week } = data;
  return (
    <div className="space-y-6" data-testid="train-lean">
      <div>
        <div className="label-eyebrow">Train · LEAN · {pw.block.track}</div>
        <h1 className="wordmark mt-3 text-4xl md:text-5xl uppercase">Your LEAN Workouts.</h1>
        <div className="mt-3 inline-flex items-center gap-2 rounded-md px-3 py-1.5 font-head uppercase tracking-widest text-xs text-white" style={{background: LEAN_PHASE_COLOR[week.phase]}}>
          <Flame size={12}/> {week.phase} · Week {week.week} · {pw.block.track}
        </div>
        <p className="mt-4 max-w-xl text-sm text-muted-foreground">Three sessions this week. Same anchors — squat, press, row, hinge. Maintaining load/reps while bodyweight decreases is a positive result.</p>
      </div>

      <div className="grid gap-4">
        {week.workouts.map((w) => (
          <div key={w.letter} className="bg-card border border-border rounded-lg p-6" data-testid={`train-lean-workout-${w.letter}`}>
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <div className="font-head uppercase text-xl">Workout {w.letter}</div>
                {w.logged && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-head uppercase tracking-widest"
                    data-testid={`train-lean-workout-${w.letter}-logged-badge`}>
                    ✓ Logged {w.logged_at}
                  </span>
                )}
              </div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">{pw.block.track}</div>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{w.focus}</p>
            {w.image && (
              <a href={w.image} target="_blank" rel="noreferrer" className="mt-4 block rounded-md overflow-hidden border border-border" data-testid={`train-lean-workout-${w.letter}-image`}>
                <img src={w.image} alt={`BA30 LEAN ${week.phase} Workout ${w.letter} — ${pw.block.track} track illustrations`} loading="lazy" className="block w-full h-auto" />
              </a>
            )}
            <table className="mt-4 w-full text-sm">
              <thead className="text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="text-left py-2">Exercise</th>
                  <th className="text-center py-2">Sets</th>
                  <th className="text-center py-2">Reps</th>
                  <th className="text-center py-2">RIR</th>
                </tr>
              </thead>
              <tbody>
                {w.exercises.map((ex, i) => (
                  <tr key={i} className="border-b border-border/60 last:border-0">
                    <td className="py-2.5 pr-2">
                      <div className="font-medium">{ex.name}</div>
                      {ex.coaching && <div className="text-[11px] text-muted-foreground mt-0.5">{ex.coaching}</div>}
                    </td>
                    <td className="text-center py-2.5">{ex.sets}</td>
                    <td className="text-center py-2.5">{ex.reps}</td>
                    <td className="text-center py-2.5">{ex.rir}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Log / Unlog Workout button */}
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              {!w.logged ? (
                <button onClick={() => logWorkout(w.letter)} disabled={busy===w.letter}
                  className="btn-primary" data-testid={`train-lean-log-workout-${w.letter}`}>
                  {busy===w.letter ? "Logging…" : `Log Workout ${w.letter}`}
                </button>
              ) : (
                <button onClick={() => unlogWorkout(w.letter)} disabled={busy===w.letter}
                  className="text-xs underline text-muted-foreground hover:text-foreground"
                  data-testid={`train-lean-unlog-workout-${w.letter}`}>
                  Undo log
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-2 text-xs uppercase tracking-widest text-muted-foreground" data-testid="train-lean-week-progress">
        {week.workouts_logged ?? 0} of {week.workouts_target ?? 3} sessions logged this week
      </div>

      <button onClick={() => nav(`/app/lean/week/${week.week}`)} className="btn-primary" data-testid="train-lean-open-week">
        Open Week {week.week} <ArrowRight size={14} className="ml-1"/>
      </button>
    </div>
  );
}

export default function Train() {
  const [data, setData] = useState({ workouts: [], current_week: 1, track: "" });
  const [history, setHistory] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [activeWeek, setActiveWeek] = useState(null);
  const [pathway, setPathway] = useState(undefined);

  const load = async () => {
    try {
      const [w, h, pw] = await Promise.all([api.get("/workouts"), api.get("/workouts/history"), api.get("/pathway/state")]);
      setData(w.data); setHistory(h.data); setPathway(pw.data.active_pathway);
      if (activeWeek === null) setActiveWeek(w.data.current_week || 1);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const complete = async (id) => {
    try { await api.post("/workouts/complete", { workout_id: id }); toast.success("Workout logged."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const byWeek = useMemo(() => {
    const acc = {};
    data.workouts.forEach((w) => { (acc[w.week] ||= []).push(w); });
    Object.values(acc).forEach((list) => list.sort((a, b) => a.template.localeCompare(b.template)));
    return acc;
  }, [data.workouts]);

  // Active pathway routing — LEAN / BUILD replace the 10K30 train view (must sit AFTER all hooks)
  if (pathway === "LEAN") return <LeanTrainView />;
  if (pathway === "BUILD") return <BuildTrainView />;

  const weekList = data.workouts.length ? [...new Set(data.workouts.map((w) => w.week))].sort((a, b) => a - b) : [];
  const selectedWeek = activeWeek ?? data.current_week ?? 1;
  const selectedPhase = data.workouts.find((w) => w.week === selectedWeek)?.phase || "RESET";
  const phaseS = PHASE_STYLE[selectedPhase] || PHASE_STYLE.RESET;
  const selectedList = byWeek[selectedWeek] || [];
  const isLocked = selectedList[0]?.locked;

  return (
    <div className="space-y-8" data-testid="train-page">
      <div>
        <div className="label-eyebrow">Train · {data.track} · Current Week {data.current_week}</div>
        <h1 className="wordmark mt-3 text-4xl md:text-5xl uppercase">Your Workouts.</h1>
        <p className="mt-2 text-muted-foreground text-sm max-w-xl">Three templates per phase — A, B, C. Repeat them each week. Progress reps first, then load.</p>
        <div className="mt-3 inline-flex items-center gap-2 rounded-md bg-foreground text-background px-3 py-1.5 text-[10px] font-head uppercase tracking-widest" data-testid="your-track-badge">
          Your Track: {data.track}
        </div>
      </div>

      {/* Week picker strip */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="label-eyebrow mb-3">Pick a week</div>
        <div className="grid grid-cols-6 md:grid-cols-12 gap-1.5" data-testid="train-week-picker">
          {weekList.map((wk) => {
            const wo = data.workouts.find((x) => x.week === wk);
            const s = PHASE_STYLE[wo?.phase] || PHASE_STYLE.RESET;
            const locked = wo?.locked;
            const active = wk === selectedWeek;
            const cls = locked ? "bg-muted text-muted-foreground border-border cursor-not-allowed opacity-60"
              : active ? `${s.bg} ${s.text} border-transparent ring-2 ring-primary`
              : `bg-background border-border hover:border-primary`;
            return (
              <button key={wk} disabled={locked} onClick={() => setActiveWeek(wk)}
                className={`aspect-square rounded-md border flex flex-col items-center justify-center relative transition-colors ${cls}`}
                data-testid={`train-week-${wk}`}>
                {locked && <Lock size={10} className="absolute top-1 right-1 opacity-70" />}
                <span className="opacity-70 text-[8px] uppercase tracking-widest">Wk</span>
                <span className="wordmark text-lg">{wk}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected week header */}
      <div className={`${phaseS.bg} ${phaseS.text} rounded-lg p-6 md:p-8`} data-testid="train-week-header">
        <div className="text-[10px] uppercase tracking-[0.22em] opacity-80">Stage · {phaseLabel(selectedPhase)}</div>
        <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="opacity-80 text-sm">Week</div>
            <div className="wordmark text-5xl md:text-7xl leading-none">{selectedWeek}<span className="opacity-50 text-2xl">/12</span></div>
          </div>
          <Link to={`/app/week/${selectedWeek}`} className="btn-secondary btn-sm !bg-transparent !border-background/60 !text-background hover:!bg-background hover:!text-foreground">
            Open full week detail →
          </Link>
        </div>
      </div>

      {/* Workouts A/B/C for the selected week */}
      {isLocked ? (
        <div className="bg-card border border-border rounded-lg p-10 text-center">
          <Lock className="mx-auto text-muted-foreground" size={28} strokeWidth={2} />
          <div className="mt-3 font-head uppercase text-lg">Week {selectedWeek} is locked</div>
          <p className="mt-2 text-sm text-muted-foreground">Complete the current week's check-in to unlock the next.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {selectedList.map((w) => (
            <div key={w.id} className="bg-card border border-border rounded-lg p-6" data-testid={`workout-${w.id}`}>
              <div className="flex justify-between items-start gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`${phaseS.bg} ${phaseS.text} inline-flex items-center rounded-md px-2 py-1 text-[10px] font-head uppercase tracking-widest`}>Workout {w.template}</span>
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Week {w.week} · {phaseLabel(w.phase)}</span>
                  </div>
                  <h3 className="wordmark mt-2 text-2xl uppercase">{w.title}</h3>
                  <p className="mt-1 text-sm text-primary">{w.cue}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setOpenId(openId === w.id ? null : w.id)}
                    className="text-[11px] uppercase tracking-widest text-primary hover:underline" data-testid={`toggle-${w.id}`}>
                    {openId === w.id ? "Hide" : "Show"} exercises
                  </button>
                  <button onClick={() => complete(w.id)} className="btn-primary btn-sm" data-testid={`complete-${w.id}`}>
                    <Check size={14} /> Log
                  </button>
                </div>
              </div>
              {openId === w.id && (
                <div className="mt-5 space-y-4">
                  {w.illustration && (
                    <div className="rounded-md overflow-hidden border border-border bg-foreground">
                      <img src={w.illustration} alt={`Week ${w.week} — Workout ${w.template} — ${w.title}`} loading="lazy" className="w-full h-auto block" data-testid={`illustration-${w.id}`} />
                    </div>
                  )}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                        <th className="py-2 pr-3">Exercise</th>
                        <th className="py-2 pr-3">Sets</th>
                        <th className="py-2 pr-3">Reps</th>
                        <th className="py-2 pr-3">RIR</th>
                        <th className="py-2 pr-3">Alt</th>
                        <th className="py-2">Coaching Focus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {w.exercises.map((ex, i) => (
                        <tr key={i} className="border-b border-border/60">
                          <td className="py-2.5 pr-3 font-head uppercase text-sm">{ex.name}</td>
                          <td className="py-2.5 pr-3">{ex.sets}</td>
                          <td className="py-2.5 pr-3">{ex.reps}</td>
                          <td className="py-2.5 pr-3">{ex.rir}</td>
                          <td className="py-2.5 pr-3 text-muted-foreground text-xs">{ex.alt}</td>
                          <td className="py-2.5 text-muted-foreground text-xs max-w-xs">{ex.cue || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* History */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="label-eyebrow">Recent Sessions</div>
        <ul className="mt-4 space-y-2 text-sm">
          {history.length === 0 && <li className="text-muted-foreground">No completions yet.</li>}
          {history.map((h) => (
            <li key={h.id} className="flex flex-wrap justify-between gap-2 border-b border-border pb-2" data-testid={`history-${h.id}`}>
              <div className="min-w-0">
                <div className="font-head uppercase text-sm">{h.title || "Workout"}</div>
                {(h.week || h.phase || h.template) && (
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-0.5">
                    {h.template ? `Workout ${h.template} · ` : ""}Week {h.week ?? "—"}{h.phase ? ` · ${h.phase}` : ""}
                  </div>
                )}
              </div>
              <span className="text-muted-foreground text-xs shrink-0">{new Date(h.date).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
